import React, { useContext, useState } from "react";
import CustomTable from "../../components/CustomTable";
import EmployeeAdd from "./EmployeeAdd";
import useApi from "../../hooks/useApi";
import { ListEmployee, deleteEmployee } from "../../constants/API/API";
import useFilter from "../../hooks/useFilter";
import EmployeeUpdate from "./EmployeeUpdate";
import usePermissions from "../../hooks/usePermissions";
import { permissionKeys } from "../../constants/staffRole";
import { AppStateContext } from "../../context/AppContext";
import { StatusChangeEmployee } from "../../constants/API/API";
import { message, Select, Space, Button } from "antd";
import moment from "moment";
import { Switch } from "antd";
import { useEffect } from "react";
import { ListBranch } from "../../constants/API/API";
import { EditOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import DeleteRecord from "../../components/DeleteRecord";
import EmployeeView from "./EmployeeView";

const ROLE_TYPES = [
  { value: "branch", label: "Admin" },
  { value: "trainer", label: "Trainer" },
];

const Employee = () => {
  const { isAdd, isDelete, isEdit, isView } = usePermissions({
    key: permissionKeys.Employees,
    redirectPermissions: ["isView"],
  });

  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Employee Management");

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [branchList, setBranchList] = useState([]);
  const [filters, setFilters] = useFilter({
    sortField: "createdAt",
    sort: "desc",
    startDate: null,
    endDate: null,
    branchId: undefined,
    role: undefined,
  });

  useEffect(() => {
    async function fetchBranches() {
      try {
        const res = await ListBranch();
        setBranchList(res.data.list || []);
      } catch (e) {
        setBranchList([]);
      }
    }
    fetchBranches();
  }, []);

  const addEmployee = () => {
    setAddModal(true);
  };

  const handleStatusChange = async (checked, employee) => {
    console.log("Status change for employee:", employee._id, "to", checked);
    setLoading(true);
    try {
      await StatusChangeEmployee(employee._id, { isActive: checked });
      message.success(
        `Employee ${checked ? "activated" : "deactivated"} successfully`
      );
      setRefresh(new Date().getTime()); // Refresh list
    } catch (error) {
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const handleEditClick = (record) => {
    setSelectedEmployee(record);
    setEditModal(true);
  };

  const handleViewClick = (record) => {
    setSelectedEmployee(record);
    setViewModal(true);
  };

  const handleBranchFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      branchId: value,
      page: 1,
    }));
  };

  const handleRoleTypeFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      role: value,
      page: 1,
    }));
  };

  const renderFilters = () => (
    <Space>
      <Select
        allowClear
        style={{ width: 200 }}
        placeholder="Filter by Branch"
        onChange={handleBranchFilter}
        value={filters.branchId}
      >
        <Select.Option value="">All Branches</Select.Option>
        {branchList.map((branch) => (
          <Select.Option key={branch._id} value={branch._id}>
            {branch.name}
          </Select.Option>
        ))}
      </Select>
      <Select
        allowClear
        style={{ width: 200 }}
        placeholder="Filter by Role Type"
        onChange={handleRoleTypeFilter}
        value={filters.trainerId}
      >
        <Select.Option value="">All Roles</Select.Option>
        {ROLE_TYPES.map((role) => (
          <Select.Option key={role.value} value={role.value}>
            {role.label}
          </Select.Option>
        ))}
      </Select>
    </Space>
  );

  useApi({
    callback: ListEmployee,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: ({ list, totalDocs }) => {
      setData(list);
      setTotalDocs(totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_, item) => (
        <>
          <p>{item?.name}</p>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      render: (_, item) => (
        <>
          <p>
            {item?.countryCode
              ? `${item?.countryCode.includes("+") ? "" : "+"}${
                  item?.countryCode
                }`
              : ""}{" "}
            {item?.mobileNumber}
          </p>
        </>
      ),
    },
    {
      title: "Branch",
      dataIndex: "branchDetails",
      key: "branchDetails",
      render: (text, { branchDetails }) => branchDetails?.name || "-",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (text, { role }) => (role === "branch" ? "admin" : role),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleStatusChange(checked, record)}
          disabled={!isEdit}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (text ? moment(text).format("ll") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          {isView && (
            <Button
              type="primary"
              onClick={() => handleViewClick(record)}
              icon={<EyeOutlined />}
              title="View"
            />
          )}
          {isEdit && (
            <Button
              type="primary"
              onClick={() => handleEditClick(record)}
              icon={<EditOutlined />}
              title="Edit"
            />
          )}
          {isDelete && (
            <DeleteRecord
              _id={record._id}
              callback={() => deleteEmployee(record._id, { isDeleted: true })}
              setLoading={setLoading}
              setRefresh={setRefresh}
              trigger={true}
              type="Employee"
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        addFn={() => isAdd && setAddModal(true)}
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        searchField={["name", "email"]}
        searchPlaceholder={"Search by Name or Email"}
        customFilter={renderFilters}
      />
      <EmployeeAdd
        visible={addModal}
        setVisible={setAddModal}
        refresh={setRefresh}
      />
      <EmployeeUpdate
        editVisible={editModal}
        setEditVisible={setEditModal}
        selectedEmployee={selectedEmployee?._id}
        refresh={setRefresh}
      />
      {viewModal && (
        <EmployeeView
          visible={viewModal}
          setVisible={setViewModal}
          id={selectedEmployee?._id}
        />
      )}
    </>
  );
};

export default Employee;
